
import React from "react";
import { Button } from "@/components/ui/button";
import { useSubmitForm } from "@/hooks/useSubmitForm";
import { validateBusinessForm } from "@/utils/formValidation";
import { submitBusiness } from "@/services/business/submissionService";
import BusinessInfoSection from "@/components/forms/sections/BusinessInfoSection";
import TagsAndMediaSection from "@/components/forms/sections/TagsAndMediaSection";
import ContactInfoSection from "@/components/forms/sections/ContactInfoSection";
import SocialMediaSection from "@/components/forms/sections/SocialMediaSection";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

const SubmitForm: React.FC = () => {
  const {
    formData,
    formErrors,
    isSubmitting,
    setFormErrors,
    setIsSubmitting,
    handleChange,
    handleCategoryChange,
    handleMediaChange,
    resetForm,
    toast,
    navigate
  } = useSubmitForm();
  
  const [submissionError, setSubmissionError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset previous submission error
    setSubmissionError(null);
    
    // Log form data for debugging
    console.log("Form submission started with data:", formData);
    
    if (formData.mediaFiles.length > 0) {
      console.log("Media files included:", formData.mediaFiles.map(f => ({
        name: f.name, 
        type: f.type, 
        size: `${(f.size / (1024 * 1024)).toFixed(2)}MB`
      })));
    }
    
    const validation = validateBusinessForm(formData);
    
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      console.error("Form validation failed:", validation.errors);
      toast({
        title: "Form validation error",
        description: "Please correct the errors in the form",
        variant: "destructive",
      });
      
      // Scroll to the first error
      const firstErrorField = Object.keys(validation.errors)[0];
      const errorElement = document.getElementById(firstErrorField);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      
      return;
    }
    
    setIsSubmitting(true);
    console.log("Starting submission process...");
    
    try {
      // Convert comma-separated tags to an array
      const tagsArray = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      
      // Determine region from city if applicable for UK city-regions
      const ukCityRegions = ['Manchester', 'London', 'Birmingham', 'Glasgow', 'Edinburgh', 'Cardiff', 'Belfast'];
      const region = ukCityRegions.includes(formData.city) ? formData.city : undefined;
      
      // Validate required fields one more time
      if (!formData.name || !formData.description || !formData.city || !formData.category) {
        throw new Error("Missing required fields");
      }
      
      console.log("Submitting business with approved=false to comply with RLS");
      
      const result = await submitBusiness({
        name: formData.name,
        description: formData.description,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        region: region,
        postcode: formData.postcode || undefined,
        what3words: formData.what3words || undefined,
        category: formData.category as any,
        tags: tagsArray,
        website: formData.website || null,
        phone: formData.phone || null,
        email: formData.email || null,
        mediaFiles: formData.mediaFiles.length > 0 ? formData.mediaFiles : undefined,
        facebook: formData.facebook || null,
        instagram: formData.instagram || null,
        tiktok: formData.tiktok || null,
        // Explicitly set approved to false to satisfy RLS policy
        approved: false
      });
      
      console.log("Submission result:", result);
      
      toast({
        title: "Submission received!",
        description: "Thank you for contributing to our eco-friendly database. Your listing will be reviewed shortly.",
      });
      
      // Reset form after successful submission
      resetForm();
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (error: any) {
      console.error("Error submitting form:", error);
      
      let errorMessage = "There was an error submitting your listing. Please try again.";
      
      // Check for specific error types
      if (error?.message) {
        if (error.message.includes("permission") || error.message.includes("row security")) {
          errorMessage = "Database permission error. Please ensure your RLS policies are correctly set up.";
        } else if (error.message.includes("not authorized") || error.message.includes("violates row-level security")) {
          errorMessage = "Database authorization error. This could be related to RLS policies or submission workflow.";
        } else if (error.message.includes("Failed to prepare storage")) {
          errorMessage = "We couldn't prepare storage for your images. Please try again later or contact support.";
        } else {
          // Use the actual error message if available
          errorMessage = error.message;
        }
      }
      
      setSubmissionError(errorMessage);
      
      toast({
        title: "Submission failed",
        description: errorMessage,
        variant: "destructive",
      });
      
      // Scroll to the error message
      const errorElement = document.querySelector('.alert-destructive');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {submissionError && (
        <Alert variant="destructive" className="mb-6 alert-destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {submissionError}
          </AlertDescription>
        </Alert>
      )}
      
      <BusinessInfoSection 
        name={formData.name}
        description={formData.description}
        address={formData.address}
        city={formData.city}
        state={formData.state}
        postcode={formData.postcode}
        what3words={formData.what3words}
        category={formData.category}
        handleChange={handleChange}
        handleCategoryChange={handleCategoryChange}
        formErrors={formErrors}
      />

      <TagsAndMediaSection
        tags={formData.tags}
        mediaFiles={formData.mediaFiles}
        handleChange={handleChange}
        handleMediaChange={handleMediaChange}
        formErrors={formErrors}
      />

      <ContactInfoSection
        website={formData.website}
        phone={formData.phone}
        email={formData.email}
        handleChange={handleChange}
        formErrors={formErrors}
      />

      <SocialMediaSection
        facebook={formData.facebook}
        instagram={formData.instagram}
        tiktok={formData.tiktok}
        handleChange={handleChange}
        formErrors={formErrors}
      />

      <Button 
        type="submit" 
        className="w-full bg-eco-500 hover:bg-eco-600"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Submitting..." : "Submit Listing"}
      </Button>
    </form>
  );
};

export default SubmitForm;
